import React, { Suspense, lazy } from 'react';
/**
 * QuestionRendererFactory — Routes question type → renderer component.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s66.
 * v3.1 (mei 2026): Added Suspense boundary for lazy-loaded renderers.
 */
import { QuestionRendererProps } from './types';
import { getQuestionTypeConfig } from '../../../content';
import { QuestionErrorBoundary } from '../QuestionErrorBoundary';
import { warn, error as logError } from '../../../utils/logger';
/** Minimal inline spinner shown while a renderer chunk loads (first use only). */
function RendererLoadingSpinner({
  moduleColor
}: {
  moduleColor?: string;
}) {
  const color = moduleColor || '#6366f1';
  return <div className="flex items-center justify-center py-12" role="status" aria-label="Vraag laden..." data-id="element-10913">
      <div className="w-8 h-8 border-4 rounded-full animate-spin" style={{
      borderColor: `${color}33`,
      borderTopColor: color
    }} data-id="element-10914" />
    </div>;
}
// F-QA-6: explicit displayName so production stacks & React DevTools surface
// a useful name instead of the bundler's mangled anonymous identifier.
RendererLoadingSpinner.displayName = 'RendererLoadingSpinner';
interface FactoryProps extends QuestionRendererProps {
  onSkipQuestion?: () => void;
}
/**
 * Routes question type → renderer component, wrapped in a per-renderer
 * error boundary so a single renderer crash shows a skip-friendly message
 * instead of taking down the entire session.
 */
export function QuestionRendererFactory(props: FactoryProps) {
  const {
    question,
    moduleColor
  } = props;
  const questionType = question.questionType || 'multiple_choice';
  let config: ReturnType<typeof getQuestionTypeConfig> | null = null;
  try {
    config = getQuestionTypeConfig(questionType);
  } catch (err) {
    logError(`[RendererFactory] Failed to load config for questionType "${questionType}" (question ${question.id}):`, err);
  }
  const Renderer = config?.Renderer;
  if (!Renderer) {
    logError(`[RendererFactory] No renderer found for questionType "${questionType}" (question ${question.id})`);
    return <div className="p-4 bg-red-50 rounded-xl text-red-700 font-bold" data-id="element-10915">
        ⚠️ Renderer niet gevonden voor type "{questionType}"
      </div>;
  }
  const rendererName = Renderer.displayName || Renderer.name || questionType;
  return <QuestionErrorBoundary questionId={question.id} onReset={() => {
    logError(`[RendererCrash] Renderer "${rendererName}" (type: ${questionType}) crashed for question ${question.id} (family: ${question.familyId ?? 'unknown'}, tier: ${question.tier ?? '?'})`);
    warn(`Question ${question.id} renderer "${rendererName}" crashed and was reset`);
  }} onSkipQuestion={props.onSkipQuestion} data-id="element-10916">
      <Suspense fallback={<RendererLoadingSpinner moduleColor={moduleColor} data-id="element-10918" />} data-id="element-10917">
        <Renderer {...props} moduleColor={moduleColor} data-id="element-10919" />
      </Suspense>
    </QuestionErrorBoundary>;
}